import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  File as FileIcon,
  FileText,
  Folder,
  FolderTree,
  Image as ImageIcon,
  Loader2,
  Plug,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { z } from "zod";
import { companiesQueries, isManager, type CompanyDto } from "@/lib/companies/api";
import {
  driveApi,
  driveQueries,
  formatBytes,
  isImage,
  isPdf,
  type DriveFileEntryDto,
} from "@/lib/drive/api";
import { useToastMutation } from "@/lib/mutations";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/lib/utils";
import { formatRelative } from "@/lib/format";

const searchSchema = z.object({
  companyId: z.string().optional(),
});

export const Route = createFileRoute("/o/$slug/drive")({
  validateSearch: searchSchema,
  component: DriveBrowserPage,
});

function DriveBrowserPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const companies = useQuery(companiesQueries.all());
  const activeCompanyId = search.companyId ?? companies.data?.[0]?.id;

  useEffect(() => {
    if (!search.companyId && activeCompanyId) {
      void navigate({ search: (prev) => ({ ...prev, companyId: activeCompanyId }), replace: true });
    }
  }, [search.companyId, activeCompanyId, navigate]);

  return (
    <PageContainer>
      <PageHeader
        title="Drive Browser"
        description="Verbundenes Google-Drive-Verzeichnis je Unternehmen. Upload und manuelles Durchsuchen."
      />

      <CompanyTabs
        companies={companies.data ?? []}
        loading={companies.isLoading}
        error={companies.error}
        activeId={activeCompanyId}
        onChange={(id) =>
          void navigate({ search: (prev) => ({ ...prev, companyId: id }), replace: false })
        }
      />

      {activeCompanyId ? (
        <DriveForCompany
          company={companies.data?.find((c) => c.id === activeCompanyId)}
          companyId={activeCompanyId}
        />
      ) : (
        !companies.isLoading && (
          <p className="mt-6 text-sm text-muted-foreground">
            Keine Unternehmen für diese Organisation sichtbar.
          </p>
        )
      )}
    </PageContainer>
  );
}

// ── Company tab strip ──────────────────────────────────────────────────────

function CompanyTabs({
  companies,
  loading,
  error,
  activeId,
  onChange,
}: {
  companies: CompanyDto[];
  loading: boolean;
  error: Error | null;
  activeId: string | undefined;
  onChange: (companyId: string) => void;
}) {
  if (loading) {
    return (
      <div className="mb-4 flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-32" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <p className="mb-4 text-sm text-destructive">
        Konnte Unternehmen nicht laden: {(error as Error).message}
      </p>
    );
  }
  if (companies.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 border-b">
      {companies.map((c) => {
        const active = c.id === activeId;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onChange(c.id)}
            className={cn(
              "relative -mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              active
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

// ── Drive for one company ──────────────────────────────────────────────────

function DriveForCompany({
  company,
  companyId,
}: {
  company: CompanyDto | undefined;
  companyId: string;
}) {
  const canManage = company ? isManager(company.role) : false;
  const status = useQuery(driveQueries.status(companyId));

  if (status.isLoading) return <Skeleton className="h-48" />;

  if (status.error) {
    return (
      <ErrorBox error={status.error as Error} onRetry={() => void status.refetch()} />
    );
  }

  if (!status.data?.connected) {
    return <NotConnectedState companyId={companyId} canManage={canManage} />;
  }

  return <ConnectedBrowser companyId={companyId} canManage={canManage} status={status.data} />;
}

// ── Not connected ──────────────────────────────────────────────────────────

function NotConnectedState({
  companyId,
  canManage,
}: {
  companyId: string;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  // Hold a reference to the OAuth popup so we can re-focus it if the button
  // is clicked twice + so we can cleanly close it on success/error.
  const popupRef = useRef<Window | null>(null);

  // Listen for the postMessage that /api/drive/callback fires when the OAuth
  // dance finishes (see CompanyDriveController.RenderCallbackPage). Filtering
  // by `source: "javeed-drive-oauth"` avoids picking up messages from
  // unrelated origins (Stripe popups, devtools, etc.). Both success and error
  // paths invalidate the status query so the UI reflects the outcome.
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const data = ev.data as { source?: string; status?: string } | null;
      if (!data || data.source !== "javeed-drive-oauth") return;
      queryClient.invalidateQueries({
        queryKey: driveQueries.status(companyId).queryKey,
      });
      if (data.status === "error") {
        toast.error("Drive-Verbindung fehlgeschlagen.");
      }
      // Popup self-closes on success, but if it failed the user might still
      // be looking at an error page — close it for them.
      try {
        popupRef.current?.close();
      } catch {
        // best-effort
      }
      popupRef.current = null;
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [companyId, queryClient]);

  const connect = useToastMutation({
    mutationFn: () => driveApi.startConnect(companyId),
    toasts: { success: false, error: false },
    onSuccess: (data) => {
      // Mock-API short-circuit: the dev-mocks endpoint marks the connection
      // as established immediately and returns a sentinel URL. Skip the
      // popup and just refresh the status so the connected listing renders.
      if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === "true") {
        queryClient.invalidateQueries({
          queryKey: driveQueries.status(companyId).queryKey,
        });
        return;
      }

      // Real flow: open Google's consent URL in a popup. The callback page
      // postMessages back to window.opener (this tab) and self-closes —
      // navigating the SPA itself away would leave the user stranded on the
      // server-rendered callback page with no way back.
      const w = 540;
      const h = 720;
      const left = Math.max(0, Math.floor((window.screen.availWidth - w) / 2));
      const top = Math.max(0, Math.floor((window.screen.availHeight - h) / 2));
      const popup = window.open(
        data.authorizationUrl,
        "javeed-drive-oauth",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,status=no,location=no`,
      );
      if (!popup) {
        toast.error("Popup blockiert. Bitte für Javeed erlauben und erneut versuchen.");
        return;
      }
      popupRef.current = popup;
      popup.focus();
    },
  });

  return (
    <div className="rounded-md border bg-card p-10 text-center">
      <FolderTree className="mx-auto h-10 w-10 text-muted-foreground" />
      <h2 className="mt-4 text-lg font-medium">Drive noch nicht verbunden</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Verknüpfe ein Google-Konto, um Dokumente automatisch im Drive-Ordner dieses Unternehmens
        abzulegen und manuell zu durchsuchen.
      </p>
      <Button
        className="mt-6"
        onClick={() => connect.mutate(undefined)}
        disabled={connect.isPending || !canManage}
        title={!canManage ? "Nur Manager können Drive verbinden." : undefined}
      >
        {connect.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plug className="h-4 w-4" />
        )}
        Mit Google verbinden
      </Button>
      {!canManage && (
        <p className="mt-3 text-xs text-muted-foreground">
          Du kannst Drive-Inhalte ansehen, sobald ein Manager die Verbindung eingerichtet hat.
        </p>
      )}
    </div>
  );
}

// ── Connected browser ──────────────────────────────────────────────────────

interface BreadcrumbEntry {
  id: string | null; // null = root
  name: string;
}

function ConnectedBrowser({
  companyId,
  canManage,
  status,
}: {
  companyId: string;
  canManage: boolean;
  status: {
    googleEmail: string | null;
    rootFolderName: string | null;
    rootFolderId: string | null;
  };
}) {
  const queryClient = useQueryClient();
  // Breadcrumb history — root + the folders the user descended into. Top of
  // the stack is the current folder.
  const [crumbs, setCrumbs] = useState<BreadcrumbEntry[]>([
    { id: null, name: status.rootFolderName ?? "Stamm" },
  ]);
  const current = crumbs[crumbs.length - 1];

  const listing = useQuery(driveQueries.files(companyId, current.id));

  const enterFolder = (entry: DriveFileEntryDto) => {
    setCrumbs((c) => [...c, { id: entry.id, name: entry.name }]);
  };
  const navigateTo = (idx: number) => {
    setCrumbs((c) => c.slice(0, idx + 1));
  };

  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: driveQueries.files(companyId, current.id).queryKey,
    });

  const disconnect = useToastMutation({
    mutationFn: () => driveApi.disconnect(companyId),
    toasts: { success: "Drive getrennt." },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: driveQueries.status(companyId).queryKey });
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useToastMutation({
    mutationFn: (file: File) => driveApi.upload(companyId, current.id, file),
    toasts: { success: "Datei hochgeladen." },
    onSuccess: () => refresh(),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Breadcrumbs crumbs={crumbs} onJump={navigateTo} />
        <div className="flex items-center gap-2">
          {canManage && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                  // Reset so the same file can be re-selected
                  e.target.value = "";
                }}
              />
              <Button
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={upload.isPending}
              >
                {upload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Hochladen
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refresh()}
            disabled={listing.isFetching}
          >
            {listing.isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Aktualisieren
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card text-sm">
        <div className="flex items-center justify-between border-b px-3 py-2 text-xs text-muted-foreground">
          <span>
            Verbunden mit <span className="font-medium text-foreground">{status.googleEmail ?? "—"}</span>
          </span>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Drive-Verbindung wirklich trennen?")) {
                  disconnect.mutate(undefined);
                }
              }}
              className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-destructive hover:bg-destructive/10"
              disabled={disconnect.isPending}
            >
              <Trash2 className="h-3 w-3" />
              Trennen
            </button>
          )}
        </div>

        {listing.isLoading && (
          <div className="space-y-2 p-3">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        )}

        {listing.error && (
          <div className="p-4 text-sm text-destructive">
            Konnte Ordner nicht laden: {(listing.error as Error).message}
          </div>
        )}

        {listing.data && (
          <FileListing
            entries={listing.data.entries}
            onFolderClick={enterFolder}
          />
        )}
      </div>
    </div>
  );
}

// ── Breadcrumbs ────────────────────────────────────────────────────────────

function Breadcrumbs({
  crumbs,
  onJump,
}: {
  crumbs: BreadcrumbEntry[];
  onJump: (idx: number) => void;
}) {
  return (
    <nav className="flex flex-wrap items-center gap-1 text-sm" aria-label="Pfad">
      {crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        return (
          <span key={`${c.id ?? "root"}-${i}`} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            {isLast ? (
              <span className="font-medium">{c.name}</span>
            ) : (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground hover:underline"
                onClick={() => onJump(i)}
              >
                {c.name}
              </button>
            )}
          </span>
        );
      })}
      {crumbs.length > 1 && (
        <button
          type="button"
          className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => onJump(crumbs.length - 2)}
        >
          <ChevronLeft className="h-3 w-3" />
          Eins zurück
        </button>
      )}
    </nav>
  );
}

// ── File listing ───────────────────────────────────────────────────────────

function FileListing({
  entries,
  onFolderClick,
}: {
  entries: DriveFileEntryDto[];
  onFolderClick: (entry: DriveFileEntryDto) => void;
}) {
  if (entries.length === 0) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">
        Dieser Ordner ist leer.
      </div>
    );
  }
  // Folders first, then files, each alphabetical.
  const sorted = [...entries].sort((a, b) => {
    if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
    return a.name.localeCompare(b.name, "de");
  });
  return (
    <ul className="divide-y">
      {sorted.map((e) => (
        <li key={e.id}>
          <FileRow entry={e} onFolderClick={onFolderClick} />
        </li>
      ))}
    </ul>
  );
}

function FileRow({
  entry,
  onFolderClick,
}: {
  entry: DriveFileEntryDto;
  onFolderClick: (entry: DriveFileEntryDto) => void;
}) {
  const Icon = entry.isFolder
    ? Folder
    : isImage(entry.mimeType)
      ? ImageIcon
      : isPdf(entry.mimeType)
        ? FileText
        : FileIcon;

  const handleClick = () => {
    if (entry.isFolder) {
      onFolderClick(entry);
    } else if (entry.webViewLink) {
      window.open(entry.webViewLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-accent/50"
    >
      <Icon
        className={cn(
          "h-5 w-5 shrink-0",
          entry.isFolder ? "text-primary" : "text-muted-foreground",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{entry.name}</div>
        <div className="text-xs text-muted-foreground">
          {entry.isFolder ? "Ordner" : formatBytes(entry.size)}
          {entry.modifiedAt && ` · ${formatRelative(entry.modifiedAt) ?? "—"}`}
        </div>
      </div>
      {!entry.isFolder && entry.webViewLink && (
        <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

// ── Misc ───────────────────────────────────────────────────────────────────

function ErrorBox({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {status === 403
            ? "Keine Berechtigung."
            : `Konnte Drive-Status nicht laden: ${error.message}`}
        </span>
      </div>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Erneut versuchen
      </Button>
    </div>
  );
}
